import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";

import { endSessionOnUnauthorized } from "./auth/session-expiry";
import { ToastProvider } from "./components/ui/toast";
import { router } from "./router";

// CHP-310: registrations / extensions / closes used to take 1min+ to surface in
// the ledger. Diagnosis: each deal mutation DOES invalidate ["deals"], so the
// *acting* user's own session refreshes immediately. The lag is on the other
// side of the workflow — e.g. a manager watching the ledger while a partner
// registers a deal, or a server-derived status (expiry / SLA) changing under a
// query nobody just mutated. Those surfaces had no refresh path while the tab
// stayed focused, because:
//   - staleTime was 30s, and
//   - refetchOnWindowFocus (TanStack's default) only fires on an actual focus
//     change, which never happens if the user keeps staring at the ledger.
//
// The central, app-wide knobs below are the conservative half of the fix:
// `staleTime` is dropped to 15s so a window-refocus (or any remount) returns
// fresh data sooner, and `refetchOnWindowFocus` is pinned explicitly so the
// behaviour is intentional rather than relying on a library default. We
// deliberately do NOT set a global `refetchInterval` here — that would poll
// every query in the app (training, admin, assets, profiles), not just deals.
// The deals-list polling backstop belongs per-query on the ledger / feed list
// queries (owned by other lanes); see the WS-5 hand-off note for the exact
// call-sites + the `refetchInterval` value to add at consolidation.
// CHP-419: reactive half of clean session-expiry. Any query or mutation that
// fails with a 401 means the session is dead — sign the user out cleanly
// instead of leaving them on a page where every action errors. The cache-level
// onError is the one place every API call funnels through (client.ts throws
// ApiError from many call-sites, so there is no single fetch interceptor).
// endSessionOnUnauthorized ignores non-401s — a 403 `tc_required` passes
// through to its existing /accept-terms handling untouched.
const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: endSessionOnUnauthorized }),
  mutationCache: new MutationCache({ onError: endSessionOnUnauthorized }),
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 15_000,
      refetchOnWindowFocus: true,
    },
  },
});

// AuthProvider lives in main.tsx, OUTSIDE React.StrictMode — see
// the comment there for why. App.tsx assumes a wrapping AuthProvider
// is in place and just consumes the auth context.
export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <RouterProvider router={router} />
      </ToastProvider>
    </QueryClientProvider>
  );
}
